// Public-facing pipeline + market metadata for the landing page.
//
// Source of truth (replaces the legacy PITCH/PULSE/BLOCK/PIP module
// taxonomy):
//   - STRIKZA_DualSwarm_Architecture v1.0.0 §5 (routing logic)
//   - STRIKZA_POWERBALL_Architecture_v1.3.docx
//   - STRIKZA_LADDER_Architecture_PRD.docx
//   - STRIKZA_SINGLES_CHAIN_PRD_v2.docx + Multi-Sport Amendment
//   - STRIKZA_Owl_Hour_Accumulator.docx
//
// Doctrinal posture: STRIKZA's roadmap is six sports-probability
// pipelines all sharing the same 18-agent substrate. The Phase-2
// expansion to other asset classes (Polymarket, crypto, forex) is a
// downstream extension of the same engine, not the main product.

export type ModuleStatus = "live" | "next" | "soon" | "roadmap";

export interface Module {
  readonly code: string;
  readonly icon: string;
  readonly market: string;
  readonly status: ModuleStatus;
  readonly statusLbl: string;
  readonly why: string;
}

// The six doctrinal pipelines. Order is the documented build sequence
// — Daily first (every subscriber, every day), then weekend jackpot
// (POWERBALL/MEGA), then weekend climbers (LADDER), then 7-day chain
// (Singles), then late-night (Owl Hour).
export const PIPELINES: readonly Module[] = [
  {
    code: "DAILY",
    icon: "⚡",
    market: "Daily slips · ≤4 legs",
    status: "live",
    statusLbl: "Live · Claude swarm",
    why: "Every subscriber, every day. Scout gates the fixture set, A/B/C/D run in parallel, the Debate Moderator forces every claim to defend itself, Consensus Ranker hands you the slip with Kelly-sized stakes. ~$0.18 per cycle.",
  },
  {
    code: "POWERBALL 10",
    icon: "◉",
    market: "Weekend jackpot · 10 selections",
    status: "next",
    statusLbl: "World Cup 2026",
    why: "Foundation / Mid-Layer / Amplifier structure. Acca Architect escalates to the reasoning tier for assembly. Combined odds 1,500-15,000. Risk Governor enforces correlation safety + jackpot reserve. ~$0.027 per weekly cycle.",
  },
  {
    code: "MEGA 20",
    icon: "◉◉",
    market: "Weekend jackpot · 20 selections",
    status: "next",
    statusLbl: "World Cup 2026",
    why: "Anchor / Bridge / Stretch layers. Combined odds ≥500,000. Cross-slip dedup ensures no fixture appears on both POWERBALL 10 and MEGA 20. Public artifact tracked in the Daily Intelligence Ledger.",
  },
  {
    code: "LADDER",
    icon: "≡",
    market: "Weekend + midweek climbers",
    status: "soon",
    statusLbl: "Live at launch",
    why: "Five-rung structure climbing from base to peak. Full 18-agent pipeline. Rung-by-rung publication on Friday 21:00. Per-rung calibration via Calibration Agent (A-18) so thresholds adjust to the league's actual base rates.",
  },
  {
    code: "SINGLES",
    icon: "→",
    market: "7-day multi-sport chain",
    status: "soon",
    statusLbl: "Live at launch",
    why: "Singles only. Odds progression 1.40 → 1.55 → 1.65 across seven days. Multi-sport: any sport with a qualifying candidate that day. Capital protected after Day 1 — only profit_balance is risked forward.",
  },
  {
    code: "OWL HOUR",
    icon: "☾",
    market: "Late-night CONMEBOL / MLS / Liga MX",
    status: "roadmap",
    statusLbl: "Phase 2",
    why: "15:00–03:00 Accra late slate for eligible American football fixtures. Picks publish at 14:00, with the completed session available in the morning.",
  },
];

// Backwards-compatible alias. Old references to MODULES still work.
export const MODULES: readonly Module[] = PIPELINES;

// Phase-2 asset-class expansion. STRIKZA's substrate works on any
// probability-priced market — the engine is market-agnostic. These
// are downstream extensions, NOT the headline roadmap.
export interface LaterMarket {
  readonly code: string;
  readonly icon: string;
  readonly market: string;
  readonly note: string;
}

export const LATER_MARKETS: readonly LaterMarket[] = [
  {
    code: "PULSE",
    icon: "◆",
    market: "Polymarket",
    note: "Same engine, prediction-market events. User controls the wallet.",
  },
  {
    code: "BLOCK",
    icon: "₿",
    market: "Crypto",
    note: "Read-only signal intelligence. STRIKZA never executes trades.",
  },
  {
    code: "PIP",
    icon: "₣",
    market: "Forex",
    note: "Currency-pair signals. User places every trade manually.",
  },
  {
    code: "EQUITY",
    icon: "$",
    market: "GSE / NSE",
    note: "Ghana Stock Exchange + Nigerian Exchange research briefs.",
  },
];

export const STATUS_COLOR: { readonly [K in ModuleStatus]: string } = {
  live: "var(--success)",
  next: "var(--accent)",
  soon: "var(--text)",
  roadmap: "var(--text-muted)",
};

// Mock dashboard rows — show the 6 doctrinal pipelines running
// concurrently. Numbers are illustrative architecture / cost facts
// (not performance claims). The bar value is purely visual rhythm.
export interface ChainMock {
  readonly code: string;
  readonly icon: string;
  readonly market: string;
  readonly week: string;
  readonly value: string;
  readonly delta: string;
  readonly live: boolean;
  readonly bar: number;
}

export const CHAIN_MOCKS: readonly ChainMock[] = [
  {
    code: "DAILY",
    icon: "⚡",
    market: "Daily slip cycle",
    week: "Today · 18 fixtures shortlisted",
    value: "≤ 4 legs",
    delta: "Claude swarm · debate complete",
    live: true,
    bar: 0.55,
  },
  {
    code: "POWERBALL 10",
    icon: "◉",
    market: "POWERBALL 10",
    week: "Sat 08:00 publish",
    value: "10 selections",
    delta: "Pool A · combined 1,500-15,000",
    live: false,
    bar: 0.4,
  },
  {
    code: "MEGA 20",
    icon: "◉◉",
    market: "MEGA POWERBALL 20",
    week: "Sat 08:00 publish",
    value: "20 selections",
    delta: "Pool B · combined ≥ 500,000",
    live: false,
    bar: 0.6,
  },
  {
    code: "LADDER",
    icon: "≡",
    market: "Weekend climbers",
    week: "Fri 21:00 publish",
    value: "5 rungs",
    delta: "Calibration A-18 · thresholds tuned",
    live: false,
    bar: 0.45,
  },
  {
    code: "SINGLES",
    icon: "→",
    market: "7-day chain",
    week: "Day 4 of 7",
    value: "1.40 → 1.55 → 1.65",
    delta: "Capital protected · profit rolling",
    live: false,
    bar: 0.5,
  },
  {
    code: "OWL HOUR",
    icon: "☾",
    market: "Owl Hour · 15:00 Accra",
    week: "Tonight · 4 tiers",
    value: "CONMEBOL / MLS / Liga MX",
    delta: "Morning reveal · 07:00 WAT",
    live: false,
    bar: 0.35,
  },
];
